import {
  type UseMutationOptions,
  type UseQueryOptions,
  keepPreviousData,
  useMutation,
  useQuery,
} from '@tanstack/react-query';

// ============================================================================
// Audio/Session Management
// ============================================================================

export interface AudioRecord {
  id: string;
  user_id: string;
  patient_id?: string;
  patient_ehr_id?: string;
  patient_name?: string;
  topic?: string;
  language?: string;
  duration?: number;
  created_at: string;
  updated_at: string;
  diarized: boolean;
  transcribed: boolean;
  annotated: boolean;
  summarized: boolean;
}

export function useAudioList(filters?: Record<string, string>, options?: UseQueryOptions<AudioRecord[]>) {
  return useQuery({
    queryKey: ['audio', 'list', filters],
    queryFn: async () => {
      const params = new URLSearchParams(filters || {});
      const res = await fetch(`/api/sessions/recent?${params}`, {
        credentials: 'include', // Send cookies for authentication
      });
      if (!res.ok) throw new Error('Failed to fetch audio list');
      return res.json();
    },
    staleTime: 0, // Always consider data stale - refetch on mount
    refetchOnMount: true, // Refetch when component mounts
    ...options,
  });
}

export interface DashboardStats {
  total: number;
  today: number;
  summarized: number;
  dictations: number;
  conversations: number;
}

export interface DashboardPagination {
  page: number;
  page_size: number;
  total: number;
}

export interface DashboardResponse {
  sessions: AudioRecord[];
  pagination: DashboardPagination;
  stats: DashboardStats;
}

export function useDashboardSessions(page: number, pageSize: number, search?: string) {
  return useQuery({
    queryKey: ['dashboard', 'sessions', page, pageSize, search],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
      });
      if (search) params.set('search', search);
      const res = await fetch(`/api/sessions/dashboard?${params}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch dashboard sessions');
      return res.json() as Promise<DashboardResponse>;
    },
    placeholderData: keepPreviousData,
    refetchOnMount: true,
  });
}

export function usePatientSessions(fhirPatientId: string | undefined, options?: UseQueryOptions<AudioRecord[]>) {
  return useQuery({
    queryKey: ['audio', 'patient', fhirPatientId],
    queryFn: async () => {
      if (!fhirPatientId) return [];
      const res = await fetch(`/api/sessions/patient/${fhirPatientId}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch patient sessions');
      return res.json();
    },
    enabled: !!fhirPatientId,
    staleTime: 0,
    refetchOnMount: true,
    ...options,
  });
}

export function useAudioById(id: string, options?: UseQueryOptions<AudioRecord>) {
  return useQuery({
    queryKey: ['audio', id],
    queryFn: async () => {
      const res = await fetch(`/api/sessions/${id}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch audio');
      return res.json();
    },
    enabled: !!id,
    ...options,
  });
}

export async function createAudio(data: Partial<AudioRecord>): Promise<AudioRecord> {
  const res = await fetch('/api/sessions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      session_id: data.id,
      language: data.language ?? 'is',
      patient_id: data.patient_id,
      fhir_patient_id: data.patient_ehr_id,
      patient_name: data.patient_name,
      mode: data.diarized ? 'conversation' : 'dictation',
    }),
  });
  if (!res.ok) throw new Error(`Failed to create audio: ${res.status}`);
  const body = await res.json();
  return { ...data, id: body.session_id ?? data.id } as AudioRecord;
}

export function useCreateAudio(options?: UseMutationOptions<AudioRecord, Error, Partial<AudioRecord>>) {
  return useMutation({
    mutationFn: createAudio,
    ...options,
  });
}

export interface SaveUserSummaryVariables {
  sessionId: string;
  summaryText: string;
  templateId: number;
  source: 'save' | 'copy';
  summaryAttributed?: unknown;
  viewingAsAuthAccountId?: string | null;
  /** AI generation the summary came from (SummaryGeneration.generationId). */
  generationId?: string | null;
}

/**
 * Persist a user summary to UserSummary. `source: 'copy'` audits the copy/export
 * action (MDR traceability); `source: 'save'` persists an edit. Both POST to the
 * same endpoint — the backend branches the audit action on `source`.
 */
export function useSaveUserSummary(options?: UseMutationOptions<unknown, Error, SaveUserSummaryVariables>) {
  return useMutation({
    mutationFn: async ({
      sessionId,
      summaryText,
      templateId,
      source,
      summaryAttributed,
      viewingAsAuthAccountId,
      generationId,
    }: SaveUserSummaryVariables) => {
      const url = viewingAsAuthAccountId
        ? `/api/sessions/${sessionId}/user-summary?viewing_as_auth_account_id=${encodeURIComponent(viewingAsAuthAccountId)}`
        : `/api/sessions/${sessionId}/user-summary`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          summary_text: summaryText,
          template_id: templateId,
          source,
          ...(summaryAttributed != null ? { summary_attributed: summaryAttributed } : {}),
          ...(generationId ? { generation_id: generationId } : {}),
        }),
      });
      if (!res.ok) {
        const error = await res.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to save summary');
      }
      return res.json();
    },
    ...options,
  });
}

export function useUpdateAudio(
  options?: UseMutationOptions<AudioRecord, Error, { id: string; data: Partial<AudioRecord> }>
) {
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<AudioRecord> }) => {
      const res = await fetch(`/api/sessions/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Failed to update audio');
      return res.json();
    },
    ...options,
  });
}

// ============================================================================
// Patient Management
// ============================================================================

export interface Patient {
  id: string; // SSN/national_id - used for display and registry lookup
  dbId?: number; // Database ID from transcriber_db - used for API calls
  name: string;
  ssn: string; // Kennitala
  dob: string;
  created_at?: string;
}

export function useCreatePatient(options?: UseMutationOptions<Patient, Error, Partial<Patient>>) {
  return useMutation({
    mutationFn: async (data: Partial<Patient>) => {
      const res = await fetch('/api/patients/find-or-create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ssn: data.ssn || data.id,
          name: data.name,
          date_of_birth: data.dob,
        }),
      });
      if (!res.ok) throw new Error('Failed to create patient');
      return res.json();
    },
    ...options,
  });
}

// ============================================================================
// Todo Management
// ============================================================================

export interface TodoPatientInfo {
  id: number;
  first_name: string;
  last_name: string;
  ssn?: string;
}

export interface TodoSessionInfo {
  id: number;
  audio_id: string;
  created_at: string;
}

export interface TodoUserInfo {
  id: number;
  first_name: string;
  last_name: string;
  username: string;
}

export interface TodoItem {
  id: number;
  has_referral_attempt?: boolean;
  user_id: number;
  created_by_user_id: number | null;
  assigned_to_user_id: number | null;
  assigned_at: string | null;
  assignment_note: string | null;
  session_id: number | null;
  patient_id: number | null;
  type: string;
  title: string;
  description: string | null;
  completed: boolean;
  priority: 'low' | 'medium' | 'high';
  due_date: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  extra_data: Record<string, unknown> | null;
  patient: TodoPatientInfo | null;
  session: TodoSessionInfo | null;
  created_by: TodoUserInfo | null;
  assigned_to: TodoUserInfo | null;
}

export interface TodoFilters {
  user_id: number;
  session_id?: string;
  patient_id?: number;
  type?: string;
  completed?: boolean;
  priority?: 'low' | 'medium' | 'high';
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}

export interface TodoListResponse {
  count: number;
  todo_items: TodoItem[];
}

export function useTodoList(audioId?: string, options?: UseQueryOptions<TodoItem[]>) {
  return useQuery({
    queryKey: ['todos', 'list', audioId],
    queryFn: async () => {
      const params = audioId ? `?session_id=${audioId}` : '';
      const res = await fetch(`/api/tasks${params}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch todos');
      const body = await res.json();
      return Array.isArray(body) ? body : (body.todo_items ?? []);
    },
    ...options,
  });
}

export function useUserTodoList(
  filters: TodoFilters | undefined,
  options?: Omit<UseQueryOptions<TodoListResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['todos', 'user-list', filters],
    queryFn: async () => {
      if (!filters?.user_id) throw new Error('user_id is required');
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(filters)) {
        if (value !== undefined && value !== null) {
          // Server derives the actor's user_id from the authenticated session;
          // client-supplied user_id would be an impersonation surface.
          if (key === 'user_id') continue;
          params.set(key, String(value));
        }
      }
      const res = await fetch(`/api/tasks?${params}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch todos');
      return res.json() as Promise<TodoListResponse>;
    },
    enabled: !!filters?.user_id,
    staleTime: 30000,
    ...options,
  });
}

// ============================================================================
// Task Document Generation (letters and requests)
// ============================================================================

export type GeneratedDocumentType = 'patient_letter' | 'referral' | 'consultant_request' | 'radiology_request';

export interface GenerateDocumentVariables {
  type: GeneratedDocumentType;
  transcript: string;
  session_id?: string | null;
  patient_id?: number | null;
  patient_name?: string | null;
  specialist?: string;
  /** The task a request document is for, so several requests from one visit each get their own document. */
  task_title?: string | null;
  task_details?: string | null;
}

export interface GenerateDocumentResponse {
  status: string;
  letter?: string;
  saved_todo?: TodoItem | null;
}

export function useGenerateDocument(
  options?: UseMutationOptions<GenerateDocumentResponse, Error, GenerateDocumentVariables>
) {
  return useMutation({
    mutationFn: async (variables: GenerateDocumentVariables) => {
      const res = await fetch('/api/tasks/generate-document', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(variables),
      });
      if (!res.ok) throw new Error(`Failed to generate ${variables.type}`);
      return res.json() as Promise<GenerateDocumentResponse>;
    },
    ...options,
  });
}

export function useCreateTodo(options?: UseMutationOptions<TodoItem, Error, Partial<TodoItem>>) {
  return useMutation({
    mutationFn: async (data: Partial<TodoItem>) => {
      const res = await fetch('/api/tasks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Failed to create todo');
      return res.json();
    },
    ...options,
  });
}

export function useUpdateTodo(options?: UseMutationOptions<TodoItem, Error, { id: number; data: Partial<TodoItem> }>) {
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<TodoItem> }) => {
      const res = await fetch(`/api/tasks/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Failed to update todo');
      return res.json();
    },
    ...options,
  });
}

export function useToggleTodoCompletion(
  options?: UseMutationOptions<{ todo_item: TodoItem }, Error, { id: number; completed: boolean; user_id: number }>
) {
  return useMutation({
    // Signature keeps user_id for callers' ergonomics but does NOT send it upstream —
    // server derives user_id from the authenticated session.
    mutationFn: async ({ id, completed }: { id: number; completed: boolean; user_id: number }) => {
      const res = await fetch(`/api/tasks/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ completed }),
      });
      if (!res.ok) throw new Error('Failed to update todo');
      return res.json();
    },
    ...options,
  });
}

export function useDeleteTodo(options?: UseMutationOptions<void, Error, { id: number; user_id: number }>) {
  return useMutation({
    // user_id param kept for callers' ergonomics but NOT sent — server derives it.
    mutationFn: async ({ id }: { id: number; user_id: number }) => {
      const res = await fetch(`/api/tasks/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to delete todo');
    },
    ...options,
  });
}

// ============================================================================
// Authentication
// ============================================================================

export interface User {
  id: string;
  email: string;
  name: string;
  kennitala?: string;
}

export function useCurrentUser(options?: UseQueryOptions<User>) {
  return useQuery({
    queryKey: ['auth', 'user'],
    queryFn: async () => {
      const res = await fetch('/api/auth/user');
      if (!res.ok) {
        if (res.status === 401) return null;
        throw new Error('Failed to fetch current user');
      }
      return res.json();
    },
    retry: false,
    ...options,
  });
}
